import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { API } from 'network/api';
import { useMemo } from 'react';
import { CommonListParams, DataPagination } from 'types/common';
import { FarmerGroupV2 } from 'types/response';

type Params = CommonListParams & {
  country?: string;
  agronomist?: string;
  subDistrict?: string;
  paddyBuyer?: string;
};

const useFarmerGroup = (params: Params) => {
  const {
    country,
    search,
    sort,
    page,
    size,
    agronomist,
    subDistrict,
    paddyBuyer,
  } = params;

  const queryParams = useMemo(
    () => ({
      page,
      size,
      projection: 'CubeListWithUsers',
      sort: sort ? sort : undefined,
      ...(country && { 'country.equals': params?.country }),
      ...(search && { 'name.contains': search?.trim() }),
      ...(agronomist &&
        agronomist === '-' && { 'fieldCoordinator.specified': false }),
      ...(agronomist &&
        agronomist !== '-' && {
          'fieldCoordinatorId.in': agronomist?.trim(),
        }),
      ...(subDistrict && { 'subDistrict.in': subDistrict?.trim() }),
      ...(paddyBuyer && { 'paddyBuyerId.in': paddyBuyer?.trim() }),
    }),
    [params],
  );

  const query = useQuery<AxiosResponse<DataPagination<FarmerGroupV2>>>({
    queryKey: ['farmer-group', queryParams],
    queryFn: () => API.get('/cubes', { params: queryParams }),
    enabled: !!country,
  });

  const farmerGroupOptions = useMemo(() => {
    return query?.data?.data?.content?.map((data) => ({
      value: data.id,
      label: data.name,
    }));
  }, [query?.data]);

  return { ...query, farmerGroupOptions };
};

export default useFarmerGroup;
